import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles/DashboardPage.css';
import useServicesService from '../../services/useServicesService';
import useCompaniesService from '../../services/useCompaniesService';
import useDriversService from '../../services/useDriverService';
import showToast from '../../utils/toast';
import { Service } from '../../models/ServiceModel';
import { Driver } from '../../models/DriverModel';
import { ServiceStatus } from '../../enums/ServiceEnum';
import DonutChart from './components/DonutChart';
import DriverServicesSearch from './components/DriverServicesSearch';
import BaseSelect from '../../components/Base/BaseSelect';
import { ArrowUpRight } from 'lucide-react';

const STATUSS_CARGA: ServiceStatus[] = [
  ServiceStatus.AUTORIZADO,
  ServiceStatus.ASIGNADO,
  ServiceStatus.PREPARACION,
  ServiceStatus.PROGRAMADO,
  ServiceStatus.EN_CURSO,
  ServiceStatus.COMPLETADO,
];

const PENDING_START: ServiceStatus[] = [
  ServiceStatus.AUTORIZADO,
  ServiceStatus.ASIGNADO,
  ServiceStatus.PREPARACION,
  ServiceStatus.PROGRAMADO,
];

type FocusMode = 'sin-conductor' | 'por-iniciar' | 'con-conductor' | 'dia-completo' | 'por-conductor';

type UpcomingRow = {
  s: Service;
  dt: Date;
  leftMin: number;
};

type DriverMatrixRow = {
  driverId: number;
  driverName: string;
  entries: UpcomingRow[];
  hasUpcoming: boolean;
};

type TimelineBucket = {
  key: string;
  label: string;
  value: number;
  color: string;
};

const DashboardPage: React.FC = () => {
  const { list: listServices, assignDriver, step3 } = useServicesService();
  const { list: listCompanies } = useCompaniesService();
  const { list: listDrivers } = useDriversService();
  const navigate = useNavigate();

  const [services, setServices] = useState<Service[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>([]);
  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [focusMode, setFocusMode] = useState<FocusMode>('sin-conductor');
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [driverOptionsByCompany, setDriverOptionsByCompany] = useState<
    Record<number, Array<{ value: number; label: string }>>
  >({});
  const [driversLoadingCompanyIds, setDriversLoadingCompanyIds] = useState<number[]>([]);
  const [assigningServiceIds, setAssigningServiceIds] = useState<number[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setNowTs(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);

  const loadData = () => {
    setLoading(true);
    let done = 0;
    const checkDone = () => {
      done += 1;
      if (done === 3) setLoading(false);
    };

    // El endpoint de servicios, sin general_mode, acota por defecto a "hoy + mañana"
    // (carga diaria del backend). Forzamos general_mode + rango explícito para
    // incluir también el día anterior (lo necesita la pestaña "Por conductor").
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const toIsoDate = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const loadServicesWindow = (page: number, acc: Service[]) => {
      listServices(
        {
          page,
          page_size: 100,
          general_mode: 1,
          start_date: toIsoDate(yesterday),
          end_date: toIsoDate(tomorrow),
        },
        {
          onSuccess: (resp: any) => {
            const data = resp?.data ?? {};
            const items: Service[] = Array.isArray(data.services) ? data.services : data.items ?? [];
            const merged = [...acc, ...items];
            const p = data.pagination;
            if (p?.has_next) {
              loadServicesWindow(page + 1, merged);
            } else {
              setServices(merged);
              checkDone();
            }
          },
          onError: () => {
            showToast('No se pudo cargar servicios', 'error');
            if (acc.length) setServices(acc);
            checkDone();
          },
        }
      );
    };
    loadServicesWindow(1, []);

    listCompanies(
      {},
      {
        onSuccess: (resp: any) => {
          const data = resp?.data ?? {};
          const items = Array.isArray(data.companies) ? data.companies : data.items ?? [];
          setCompanies(items.map((c: any) => ({ id: c.id ?? c.value, name: c.name || '' })));
          checkDone();
        },
        onError: () => checkDone(),
      }
    );

    const loadDriversPage = (page: number, acc: Driver[]) => {
      listDrivers(
        { page, page_size: 200, sort_field: 'first_name', sort_order: 'asc' },
        {
          onSuccess: (resp: any) => {
            const data = resp?.data ?? {};
            const items: Driver[] = Array.isArray(data.drivers) ? data.drivers : data.items ?? [];
            const merged = [...acc, ...items];
            if (data.pagination?.has_next) {
              loadDriversPage(page + 1, merged);
            } else {
              setDrivers(merged);
              checkDone();
            }
          },
          onError: () => {
            if (acc.length) setDrivers(acc);
            checkDone();
          },
        }
      );
    };
    loadDriversPage(1, []);
  };

  const toDateTime = (s: Service): Date | null => {
    if (!s.service_date || !s.service_time) return null;
    const time = String(s.service_time).trim().slice(0, 8);
    const dt = new Date(`${s.service_date}T${time}`);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const hasDriver = (s: Service) =>
    Boolean(String(s.driver_name ?? '').trim()) ||
    (s.driver !== null && s.driver !== undefined && String(s.driver).trim() !== '' && Number(s.driver) !== 0);

  const isVip = (s: Service) => s.priority === 'VIP';

  const vipUrgency = (leftMin: number): '30' | '60' | '90' | 'upcoming' => {
    if (leftMin <= 30) return '30';
    if (leftMin <= 60) return '60';
    if (leftMin <= 90) return '90';
    return 'upcoming';
  };

  const cargaDiaria = useMemo(() => {
    const base = companyFilter === '' ? services : services.filter((s) => Number(s.company) === companyFilter);
    return base.filter((s) => s.status && STATUSS_CARGA.includes(s.status));
  }, [services, companyFilter]);

  const porIniciar = useMemo(() => {
    const horizon = nowTs + 3 * 60 * 60 * 1000;
    return cargaDiaria.filter((s) => {
      if (!s.status || !PENDING_START.includes(s.status)) return false;
      const dt = toDateTime(s);
      if (!(dt instanceof Date)) return false;
      const t = dt.getTime();
      return t >= nowTs && t <= horizon;
    });
  }, [cargaDiaria, nowTs]);

  const sinConductorServicios = useMemo(
    () => porIniciar.filter((s) => !hasDriver(s)),
    [porIniciar]
  );

  const conConductorServicios = useMemo(
    () => porIniciar.filter((s) => hasDriver(s)),
    [porIniciar]
  );

  const diaCompletoServicios = useMemo(() => {
    const base = companyFilter === '' ? cargaDiaria : cargaDiaria.filter((s) => Number(s.company) === companyFilter);
    const today = new Date(nowTs);
    today.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);

    return base.filter((s) => {
      const dt = toDateTime(s);
      if (!(dt instanceof Date)) return false;
      const t = dt.getTime();
      return t >= today.getTime() && t <= endOfDay.getTime();
    });
  }, [cargaDiaria, companyFilter, nowTs]);

  const startOfDay = (d: Date) => {
    const out = new Date(d);
    out.setHours(0, 0, 0, 0);
    return out;
  };

  const endOfDay = (d: Date) => {
    const out = new Date(d);
    out.setHours(23, 59, 59, 999);
    return out;
  };

  const isoDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const porConductorServicios = useMemo(() => {
    const base = companyFilter === '' ? cargaDiaria : cargaDiaria.filter((s) => Number(s.company) === companyFilter);
    const today = new Date(nowTs);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const from = startOfDay(yesterday);
    const to = endOfDay(tomorrow);

    return base.filter((s) => {
      const dt = toDateTime(s);
      if (!(dt instanceof Date)) return false;
      const t = dt.getTime();
      return t >= from.getTime() && t <= to.getTime();
    });
  }, [cargaDiaria, companyFilter, nowTs]);

  const matrixDayLabel = (dt: Date): 'Ayer' | 'Hoy' | 'Mañana' => {
    const today = new Date(nowTs);
    const key = isoDate(dt);
    if (key === isoDate(today)) return 'Hoy';
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (key === isoDate(yesterday)) return 'Ayer';
    return 'Mañana';
  };

  const toRows = (list: Service[]): UpcomingRow[] =>
    list
      .map((s) => {
        const dt = toDateTime(s);
        if (!(dt instanceof Date)) return null;
        return { s, dt, leftMin: (dt.getTime() - nowTs) / 60000 };
      })
      .filter((row): row is UpcomingRow => row !== null)
      .sort((a, b) => a.dt.getTime() - b.dt.getTime());

  const prioritySort = (rows: UpcomingRow[]) =>
    [...rows].sort((a, b) => {
      const aVip = isVip(a.s) ? 0 : 1;
      const bVip = isVip(b.s) ? 0 : 1;
      if (aVip !== bVip) return aVip - bVip;
      const aNo = !hasDriver(a.s) ? 0 : 1;
      const bNo = !hasDriver(b.s) ? 0 : 1;
      if (aNo !== bNo) return aNo - bNo;
      const aCrit = a.leftMin <= 60 ? 0 : 1;
      const bCrit = b.leftMin <= 60 ? 0 : 1;
      if (aCrit !== bCrit) return aCrit - bCrit;
      return a.dt.getTime() - b.dt.getTime();
    });

  const porIniciarRows = useMemo(() => toRows(porIniciar), [porIniciar, nowTs]);
  const sinConductor = useMemo(
    () => prioritySort(toRows(sinConductorServicios)),
    [sinConductorServicios, nowTs]
  );
  const conConductor = useMemo(() => toRows(conConductorServicios), [conConductorServicios, nowTs]);
  const diaCompleto = useMemo(() => toRows(diaCompletoServicios), [diaCompletoServicios, nowTs]);

  const porConductorRows = useMemo<DriverMatrixRow[]>(() => {
    const byDriver = new Map<number, DriverMatrixRow>();

    drivers.forEach((d) => {
      const driverId = Number(d.id);
      if (!Number.isFinite(driverId) || driverId <= 0) return;
      const driverName = `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim() || `Conductor #${driverId}`;
      byDriver.set(driverId, { driverId, driverName, entries: [], hasUpcoming: false });
    });

    toRows(porConductorServicios.filter(hasDriver)).forEach((row) => {
      const driverId = Number(row.s.driver);
      if (!Number.isFinite(driverId) || driverId <= 0) return;
      const driverName = String(row.s.driver_name || '').trim() || `Conductor #${driverId}`;
      const existing = byDriver.get(driverId);
      if (existing) {
        existing.entries.push(row);
      } else {
        byDriver.set(driverId, { driverId, driverName, entries: [row], hasUpcoming: false });
      }
    });

    return Array.from(byDriver.values())
      .map((row) => {
        const entries = [...row.entries].sort((a, b) => a.dt.getTime() - b.dt.getTime());
        return { ...row, entries, hasUpcoming: entries.some((e) => e.leftMin > 0) };
      })
      .sort((a, b) => {
        if (a.hasUpcoming !== b.hasUpcoming) return a.hasUpcoming ? -1 : 1;
        const aHas = a.entries.length > 0;
        const bHas = b.entries.length > 0;
        if (aHas !== bHas) return aHas ? -1 : 1;
        if (aHas && bHas) return a.entries[0].dt.getTime() - b.entries[0].dt.getTime();
        return a.driverName.localeCompare(b.driverName, 'es');
      });
  }, [drivers, porConductorServicios, nowTs]);

  const criticosSinConductor = useMemo(
    () => sinConductor.filter((row) => row.leftMin >= 0 && row.leftMin <= 60),
    [sinConductor]
  );

  /** Ventana horaria: cubre el 100% de por iniciar (ahora → +3h). */
  const timelineBuckets = useMemo<TimelineBucket[]>(() => {
    const buckets = { menos1h: 0, unaDos: 0, dosTres: 0 };
    porIniciarRows.forEach(({ leftMin }) => {
      const diffH = leftMin / 60;
      if (diffH <= 1) buckets.menos1h += 1;
      else if (diffH <= 2) buckets.unaDos += 1;
      else buckets.dosTres += 1;
    });
    return [
      { key: 'lt1', label: '≤ 1h', value: buckets.menos1h, color: '#ef4444' },
      { key: '1-2', label: '1-2h', value: buckets.unaDos, color: '#f59e0b' },
      { key: '2-3', label: '2-3h', value: buckets.dosTres, color: '#38bdf8' },
    ];
  }, [porIniciarRows]);

  const statusBreakdown = useMemo(
    () => [
      { label: 'Autorizado', value: cargaDiaria.filter((s) => s.status === ServiceStatus.AUTORIZADO).length, color: '#a78bfa' },
      { label: 'Asignado', value: cargaDiaria.filter((s) => s.status === ServiceStatus.ASIGNADO).length, color: '#60a5fa' },
      {
        label: 'Preparación',
        value: cargaDiaria.filter(
          (s) => s.status === ServiceStatus.PREPARACION || s.status === ServiceStatus.PROGRAMADO
        ).length,
        color: '#f59e0b',
      },
    ],
    [cargaDiaria]
  );

  const cargaChartTotal = statusBreakdown.reduce((acc, row) => acc + row.value, 0);

  const kpiSin = sinConductorServicios.length;
  const kpiCon = conConductorServicios.length;
  const kpiPorIniciar = porIniciar.length;

  const formatClock = (ts: number) =>
    new Date(ts).toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      hourCycle: 'h23',
    });

  const windowFrom = formatClock(nowTs);
  const windowTo = formatClock(nowTs + 3 * 60 * 60 * 1000);

  const formatLeft = (leftMin: number) => {
    if (leftMin < 0) {
      const late = Math.abs(Math.round(leftMin));
      if (late < 60) return `hace ${late} min`;
      return `hace ${Math.floor(late / 60)}h ${late % 60}m`;
    }
    const mins = Math.round(leftMin);
    if (mins < 60) return `en ${mins} min`;
    return `en ${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  const urgencyMeta = (s: Service, leftMin: number) => {
    if (isVip(s)) {
      return {
        className: `coord-urgency coord-urgency--vip coord-urgency--vip-${vipUrgency(leftMin)}`,
        label: `VIP · ${Math.max(0, Math.ceil(leftMin))} min`,
      };
    }
    if (!hasDriver(s) && leftMin < 0) {
      return { className: 'coord-urgency coord-urgency--critical', label: 'Vencido' };
    }
    if (!hasDriver(s) && leftMin <= 60) {
      return { className: 'coord-urgency coord-urgency--critical', label: 'Crítico' };
    }
    if (!hasDriver(s)) {
      return { className: 'coord-urgency coord-urgency--warn', label: 'Sin conductor' };
    }
    return { className: 'coord-urgency coord-urgency--ok', label: 'Listo' };
  };

  const rowTone = (s: Service, leftMin: number) => {
    if (isVip(s)) return `is-vip is-vip-${vipUrgency(leftMin)}`;
    if (!hasDriver(s) && leftMin < 0) return 'is-overdue';
    if (!hasDriver(s) && leftMin <= 60) return 'is-critical';
    if (!hasDriver(s)) return 'is-warn';
    return 'is-ok';
  };

  const passengersLabel = (s: Service) => {
    if (Array.isArray(s.passengers) && s.passengers.length > 0) return `${s.passengers.length} pax`;
    if (s.passenger && String(s.passenger).trim() !== '') return String(s.passenger);
    return '—';
  };

  const routeLabel = (s: Service) => {
    const origin = s.origin_name || '—';
    const destination = s.destination_name || '—';
    return `${origin} → ${destination}`;
  };

  const companyNameOf = (s: Service) => {
    const cid = Number(s.company);
    return companies.find((c) => c.id === cid)?.name || s.company_name || `Empresa ${cid || ''}`;
  };

  const statusLabel = (s: Service) => {
    switch (s.status) {
      case ServiceStatus.AUTORIZADO:
        return 'Autorizado';
      case ServiceStatus.ASIGNADO:
        return 'Asignado';
      case ServiceStatus.PREPARACION:
        return 'Preparación';
      case ServiceStatus.PROGRAMADO:
        return 'Programado';
      default:
        return s.status || '—';
    }
  };

  const openService = (s: Service) => {
    if (s.id) navigate(`/services/edit/${s.id}`);
  };

  const loadDriversForCompany = async (companyId: number) => {
    if (!companyId || driverOptionsByCompany[companyId] || driversLoadingCompanyIds.includes(companyId)) {
      return;
    }
    setDriversLoadingCompanyIds((prev) => [...prev, companyId]);
    try {
      const response: any = await step3(companyId);
      const drivers = response?.data?.drivers ?? response?.drivers ?? [];
      const options = (Array.isArray(drivers) ? drivers : [])
        .map((driver: any) => ({
          value: Number(driver?.value ?? driver?.id),
          label: String(driver?.label ?? driver?.name ?? '').trim(),
        }))
        .filter((driver: { value: number; label: string }) => Number.isFinite(driver.value) && driver.value > 0);
      setDriverOptionsByCompany((prev) => ({ ...prev, [companyId]: options }));
    } catch {
      showToast('No se pudieron cargar los conductores.', 'error');
      setDriverOptionsByCompany((prev) => ({ ...prev, [companyId]: [] }));
    } finally {
      setDriversLoadingCompanyIds((prev) => prev.filter((id) => id !== companyId));
    }
  };

  const handleAssignDriver = async (service: Service, driverId: number) => {
    const serviceId = Number(service.id);
    if (!serviceId || assigningServiceIds.includes(serviceId)) return;
    setAssigningServiceIds((prev) => [...prev, serviceId]);
    try {
      const response: any = await assignDriver(serviceId, driverId);
      if (
        !response ||
        response.success === false ||
        (typeof response.http_status === 'number' && response.http_status >= 400)
      ) {
        throw new Error(response?.message || 'No se pudo asignar el conductor.');
      }
      showToast('Conductor asignado correctamente.', 'success');
      loadData();
    } catch (error: any) {
      showToast(error?.message || 'No se pudo asignar el conductor.', 'error');
    } finally {
      setAssigningServiceIds((prev) => prev.filter((id) => id !== serviceId));
    }
  };

  const activeList =
    focusMode === 'sin-conductor'
      ? sinConductor
      : focusMode === 'con-conductor'
        ? conConductor
        : focusMode === 'dia-completo'
          ? diaCompleto
          : prioritySort(porIniciarRows);

  const queueTitle =
    focusMode === 'sin-conductor'
      ? 'Cola sin conductor'
      : focusMode === 'con-conductor'
        ? 'Con conductor asignado'
        : focusMode === 'dia-completo'
          ? 'Día completo'
          : focusMode === 'por-conductor'
            ? 'Por conductor'
            : 'Por iniciar';

  if (loading) {
    return (
      <div className="dashboard-layout coord-dashboard">
        <div className="dashboard-loading" role="status" aria-live="polite">
          <div className="modern-spinner" aria-hidden="true" />
          <p>Cargando coordinación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout coord-dashboard">
      <div className="dashboard-main">
        <div className="dashboard-content">
          <header className="coord-header">
            <div className="coord-header-text">
              <p className="coord-kicker">Operación del día</p>
              <h1 className="coord-title">Coordinación</h1>
            </div>
            <div className="coord-header-actions">
              <div className="coord-range" aria-label="Rango de por iniciar">
                <span className="coord-range-label">Por iniciar</span>
                <span className="coord-range-clock">
                  {windowFrom}
                  <span className="coord-range-sep">→</span>
                  {windowTo}
                </span>
                <span className="coord-range-hint">próximas 3 h</span>
              </div>
              <label className="coord-filter-label" htmlFor="coord-company-filter">
                Empresa
              </label>
              <select
                id="coord-company-filter"
                className="dashboard-filter-select"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Todas las empresas</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button type="button" className="coord-refresh" onClick={loadData}>
                Actualizar
              </button>
            </div>
          </header>

          {criticosSinConductor.length > 0 && (
            <div className="coord-alert" role="alert">
              <div className="coord-alert-pulse" aria-hidden="true" />
              <div className="coord-alert-body">
                <strong>{criticosSinConductor.length} crítico(s)</strong>
                <span> inician en ≤1h y siguen sin conductor. Asigna ahora.</span>
              </div>
              <button
                type="button"
                className="coord-alert-btn"
                onClick={() => setFocusMode('sin-conductor')}
              >
                Ir a la cola
              </button>
            </div>
          )}

          <section className="coord-kpi-grid" aria-label="Indicadores de coordinación">
            <button
              type="button"
              className={`coord-kpi coord-kpi--danger${focusMode === 'sin-conductor' ? ' is-active' : ''}`}
              onClick={() => setFocusMode('sin-conductor')}
              aria-pressed={focusMode === 'sin-conductor'}
            >
              <span className="coord-kpi-top">
                <span className="coord-kpi-label">Sin conductor</span>
                <span className="coord-kpi-chip coord-kpi-chip--danger">{kpiSin}</span>
              </span>
              <span className="coord-kpi-value">{kpiSin}</span>
              <span className="coord-kpi-range-inline">
                {windowFrom} → {windowTo}
              </span>
              <span className="coord-kpi-hint">
                {criticosSinConductor.length > 0
                  ? `${criticosSinConductor.length} críticos · prioridad máxima`
                  : 'Pendientes de asignación'}
              </span>
              <span className="coord-kpi-bar" aria-hidden="true">
                <span
                  style={{ width: `${kpiPorIniciar ? (kpiSin / kpiPorIniciar) * 100 : 0}%` }}
                />
              </span>
            </button>

            <button
              type="button"
              className={`coord-kpi coord-kpi--info${focusMode === 'por-iniciar' ? ' is-active' : ''}`}
              onClick={() => setFocusMode('por-iniciar')}
              aria-pressed={focusMode === 'por-iniciar'}
            >
              <span className="coord-kpi-top">
                <span className="coord-kpi-label">Por iniciar</span>
                <span className="coord-kpi-chip">{kpiPorIniciar}</span>
              </span>
              <span className="coord-kpi-value">{kpiPorIniciar}</span>
              <span className="coord-kpi-range-inline">
                {windowFrom} → {windowTo}
              </span>
              <span className="coord-kpi-hint">
                {kpiSin} sin conductor · {kpiCon} con conductor
              </span>
              <span className="coord-kpi-bar" aria-hidden="true">
                <span style={{ width: '100%' }} />
              </span>
            </button>

            <button
              type="button"
              className={`coord-kpi coord-kpi--ok${focusMode === 'con-conductor' ? ' is-active' : ''}`}
              onClick={() => setFocusMode('con-conductor')}
              aria-pressed={focusMode === 'con-conductor'}
            >
              <span className="coord-kpi-top">
                <span className="coord-kpi-label">Con conductor</span>
                <span className="coord-kpi-chip coord-kpi-chip--ok">{kpiCon}</span>
              </span>
              <span className="coord-kpi-value">{kpiCon}</span>
              <span className="coord-kpi-range-inline">
                {windowFrom} → {windowTo}
              </span>
              <span className="coord-kpi-hint">Listos en la ventana</span>
              <span className="coord-kpi-bar" aria-hidden="true">
                <span
                  style={{ width: `${kpiPorIniciar ? (kpiCon / kpiPorIniciar) * 100 : 0}%` }}
                />
              </span>
            </button>

            <div className="coord-kpi coord-kpi--chart" aria-label="Carga del día">
              <span className="coord-kpi-label">Carga del día</span>
              <div className="coord-carga-chart">
                <DonutChart
                  segments={statusBreakdown}
                  size={92}
                  strokeWidth={12}
                  centerLabel={{ title: 'Total', value: String(cargaChartTotal) }}
                />
                <ul className="coord-carga-legend">
                  {statusBreakdown.map((row) => (
                    <li key={row.label}>
                      <span className="coord-compose-dot" style={{ background: row.color }} />
                      <span>{row.label}</span>
                      <strong>{row.value}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <div className="coord-main-grid">
            <section className="coord-panel" aria-labelledby="coord-queue-title">
              <div className="coord-panel-head">
                <div>
                  <h2 id="coord-queue-title" className="coord-panel-title">
                    {queueTitle}
                  </h2>
                  <p className="coord-panel-sub">
                    {focusMode === 'dia-completo' ? (
                      <span className="coord-range-mini">00:00 → 23:59</span>
                    ) : focusMode === 'por-conductor' ? (
                      <span className="coord-range-mini">Ayer + hoy + mañana · 72 h</span>
                    ) : (
                      <span className="coord-range-mini">{windowFrom} → {windowTo}</span>
                    )}
                    {' · '}
                    {focusMode === 'por-conductor'
                      ? `${porConductorRows.length} conductor${porConductorRows.length === 1 ? '' : 'es'}`
                      : `${activeList.length} servicio${activeList.length === 1 ? '' : 's'}`}
                  </p>
                </div>
                <div className="coord-panel-tabs" role="tablist" aria-label="Vista de cola">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={focusMode === 'sin-conductor'}
                    className={`coord-tab${focusMode === 'sin-conductor' ? ' is-active' : ''}`}
                    onClick={() => setFocusMode('sin-conductor')}
                  >
                    Sin ({kpiSin})
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={focusMode === 'por-iniciar'}
                    className={`coord-tab${focusMode === 'por-iniciar' ? ' is-active' : ''}`}
                    onClick={() => setFocusMode('por-iniciar')}
                  >
                    Por iniciar ({kpiPorIniciar})
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={focusMode === 'con-conductor'}
                    className={`coord-tab${focusMode === 'con-conductor' ? ' is-active' : ''}`}
                    onClick={() => setFocusMode('con-conductor')}
                  >
                    Con ({kpiCon})
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={focusMode === 'dia-completo'}
                    className={`coord-tab${focusMode === 'dia-completo' ? ' is-active' : ''}`}
                    onClick={() => setFocusMode('dia-completo')}
                  >
                    Día completo ({diaCompleto.length})
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={focusMode === 'por-conductor'}
                    className={`coord-tab${focusMode === 'por-conductor' ? ' is-active' : ''}`}
                    onClick={() => setFocusMode('por-conductor')}
                  >
                    Por conductor ({porConductorRows.length})
                  </button>
                </div>
              </div>

              {focusMode === 'por-conductor' ? (
                porConductorRows.length === 0 ? (
                  <div className="coord-empty">
                    <p>No hay conductores registrados.</p>
                  </div>
                ) : (
                  <div className="coord-matrix" aria-label="Servicios por conductor (ayer, hoy y mañana)">
                    {porConductorRows.map((row) => (
                      <div
                        key={row.driverId}
                        className={`coord-matrix-row${row.hasUpcoming ? ' has-upcoming' : ''}`}
                      >
                        <div className="coord-matrix-driver">
                          <span className="coord-matrix-driver-name">{row.driverName}</span>
                          <span className="coord-matrix-driver-count">
                            {row.entries.length} servicio{row.entries.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        {row.entries.length === 0 ? (
                          <span className="coord-matrix-empty">Sin servicios entre ayer y mañana</span>
                        ) : (
                          <div className="coord-matrix-track">
                            {row.entries.map(({ s, dt, leftMin }) => {
                              const dayLabel = matrixDayLabel(dt);
                              const isUpcoming = leftMin > 0;
                              const dayTone =
                                dayLabel === 'Hoy' ? 'today' : dayLabel === 'Mañana' ? 'tomorrow' : 'yesterday';
                              return (
                                <button
                                  key={s.id}
                                  type="button"
                                  className={`coord-matrix-chip is-${dayTone}${isUpcoming ? ' is-upcoming' : ''}`}
                                  onClick={() => openService(s)}
                                  title={`${companyNameOf(s)} · ${routeLabel(s)} · ${statusLabel(s)}`}
                                >
                                  <span className="coord-matrix-chip-top">
                                    <span className="coord-matrix-chip-day">{dayLabel}</span>
                                    <span className="coord-matrix-chip-time">{formatClock(dt.getTime())}</span>
                                  </span>
                                  <span className="coord-matrix-chip-company">{companyNameOf(s)}</span>
                                  <span className="coord-matrix-chip-route">{routeLabel(s)}</span>
                                  <span className="coord-matrix-chip-pax">{passengersLabel(s)}</span>
                                  <span className="coord-matrix-chip-link">
                                    Abrir servicio
                                    <ArrowUpRight size={12} strokeWidth={2.5} aria-hidden />
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              ) : activeList.length === 0 ? (
                <div className="coord-empty">
                  <p>
                    {focusMode === 'sin-conductor'
                      ? 'Sin pendientes de conductor en las próximas 3 horas.'
                      : focusMode === 'con-conductor'
                        ? 'No hay servicios con conductor en las próximas 3 horas.'
                        : focusMode === 'dia-completo'
                          ? 'No hay servicios para el día completo.'
                          : 'No hay servicios por iniciar en las próximas 3 horas.'}
                  </p>
                </div>
              ) : (
                <ul
                  className="coord-queue"
                  aria-label={queueTitle}
                >
                  {activeList.map(({ s, dt, leftMin }) => {
                    const urgency = urgencyMeta(s, leftMin);
                    const companyId = Number(s.company);
                    const canAssignDriver =
                      s.status === ServiceStatus.AUTORIZADO &&
                      !hasDriver(s) &&
                      Number.isFinite(companyId) &&
                      companyId > 0;
                    const isLoadingDrivers = driversLoadingCompanyIds.includes(companyId);
                    const isAssigning = assigningServiceIds.includes(Number(s.id));
                    const driverOptions = driverOptionsByCompany[companyId] ?? [];
                    return (
                      <li key={s.id ?? `${companyNameOf(s)}-${dt.getTime()}`}>
                        <div
                          role="button"
                          tabIndex={0}
                          className={`coord-queue-item ${rowTone(s, leftMin)}`}
                          onClick={() => openService(s)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openService(s);
                            }
                          }}
                          aria-label={`Abrir servicio ${companyNameOf(s)} a las ${formatClock(dt.getTime())}`}
                        >
                          <div className="coord-queue-time">
                            <span className="coord-queue-clock">
                              {formatClock(dt.getTime())}
                            </span>
                            <span className="coord-queue-left">{formatLeft(leftMin)}</span>
                          </div>
                          <div className="coord-queue-body">
                            <div className="coord-queue-topline">
                              <span className="coord-queue-company">{companyNameOf(s)}</span>
                              {isVip(s) && <span className="coord-vip-flag">VIP</span>}
                              <span className="coord-queue-status">{statusLabel(s)}</span>
                            </div>
                            <span className="coord-queue-route">{routeLabel(s)}</span>
                            <span className="coord-queue-meta">
                              {passengersLabel(s)}
                              {' · '}
                              {hasDriver(s) ? s.driver_name || 'Conductor asignado' : 'Sin conductor'}
                            </span>
                          </div>
                          <div className="coord-queue-side">
                            <span className={urgency.className}>{urgency.label}</span>
                            {canAssignDriver ? (
                              <div
                                className="coord-assign-driver"
                                onClick={(event) => event.stopPropagation()}
                                onKeyDown={(event) => event.stopPropagation()}
                              >
                                <BaseSelect
                                  label="Asignar conductor"
                                  name={`assign-driver-${s.id}`}
                                  value=""
                                  hideLabel
                                  size="compact"
                                  searchable
                                  preserveCase
                                  menuMinWidth={300}
                                  includePlaceholderOption={false}
                                  disabled={isAssigning}
                                  placeholder={
                                    isAssigning
                                      ? 'Asignando…'
                                      : isLoadingDrivers
                                        ? 'Cargando…'
                                        : 'Asignar conductor'
                                  }
                                  searchPlaceholder="Buscar conductor…"
                                  options={driverOptions}
                                  onOpenChange={(open) => {
                                    if (open) void loadDriversForCompany(companyId);
                                  }}
                                  onChange={(event) => {
                                    const driverId = Number(event.target.value);
                                    if (Number.isFinite(driverId) && driverId > 0) {
                                      void handleAssignDriver(s, driverId);
                                    }
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="coord-queue-action">Abrir</span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="coord-quickbar">
                <button type="button" className="coord-link-btn" onClick={() => navigate('/services')}>
                  Servicios
                </button>
                <button type="button" className="coord-link-btn" onClick={() => navigate('/drivers')}>
                  Conductores
                </button>
              </div>
            </section>

            <aside className="coord-side" aria-label="Resumen de carga">
              <section className="coord-panel coord-panel--compact">
                <div className="coord-panel-head-inline">
                  <h2 className="coord-panel-title">Ventana horaria</h2>
                  <span className="coord-range-mini">
                    {windowFrom} → {windowTo}
                  </span>
                </div>
                <div className="coord-timeline" aria-label="Distribución horaria de por iniciar">
                  {timelineBuckets.map((bucket) => {
                    const max = Math.max(1, ...timelineBuckets.map((b) => b.value));
                    const pct = (bucket.value / max) * 100;
                    return (
                      <div key={bucket.key} className="coord-timeline-row">
                        <span className="coord-timeline-label">{bucket.label}</span>
                        <div className="coord-timeline-track">
                          <div
                            className="coord-timeline-fill"
                            style={{ width: `${pct}%`, background: bucket.color }}
                          />
                        </div>
                        <span className="coord-timeline-value">{bucket.value}</span>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="coord-panel coord-panel--compact coord-panel--carga">
                <h2 className="coord-panel-title">Carga del día</h2>
                <div className="coord-carga-panel">
                  <DonutChart
                    segments={statusBreakdown}
                    size={128}
                    strokeWidth={14}
                    centerLabel={{ title: 'Total', value: String(cargaChartTotal) }}
                  />
                  <ul className="coord-carga-legend coord-carga-legend--panel">
                    {statusBreakdown.map((row) => (
                      <li key={row.label}>
                        <span className="coord-compose-dot" style={{ background: row.color }} />
                        <span>{row.label}</span>
                        <strong>{row.value}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <DriverServicesSearch />
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
